import { timezoneGroupsService } from './timezoneGroups.js';

// Preferences are stored as plaintext JSON in localStorage. They only hold
// timezone display settings (zone list, time format, dark mode) — nothing
// sensitive. An earlier version AES-"encrypted" them under
// `zonewise_secure_preferences_*`, but the key was derived entirely from
// public browser-fingerprint data, so it provided no real protection (SEC-8).
class UserPreferencesService {
  constructor() {
    this.storageKey = 'zonewise_preferences';
    this.legacyEncryptedKeyPrefix = 'zonewise_secure_preferences';
    this.defaultPreferences = {
      selectedTimezones: [],
      timeFormat: '12h',
      language: 'en',
      homeTimezone: null,
      darkMode: false,
      cityPresets: [],
      activeGroupId: null,
      lastUpdated: null
    };
  }

  storageKeyFor(userId = null) {
    return userId ? `${this.storageKey}_${userId}` : this.storageKey;
  }

  // Drop the legacy AES blob — it can't be read without crypto-js, and the
  // user just falls back to defaults once (display prefs only).
  removeLegacyEncryptedData(userId) {
    localStorage.removeItem(`${this.legacyEncryptedKeyPrefix}_${userId}`);
  }

  getPreferences(userId = null) {
    try {
      if (userId) {
        this.removeLegacyEncryptedData(userId);
      }

      const stored = localStorage.getItem(this.storageKeyFor(userId));
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...this.defaultPreferences,
          ...parsed
        };
      }

      return this.defaultPreferences;
    } catch (error) {
      console.error('Error getting preferences:', error);
      return this.defaultPreferences;
    }
  }

  savePreferences(preferences, userId = null) {
    try {
      const updatedPreferences = {
        ...preferences,
        lastUpdated: new Date().toISOString()
      };

      localStorage.setItem(this.storageKeyFor(userId), JSON.stringify(updatedPreferences));

      return updatedPreferences;
    } catch (error) {
      console.error('Error saving preferences:', error);
      throw error;
    }
  }

  updatePreferences(updates, userId = null) {
    const current = this.getPreferences(userId);
    const updated = {
      ...current,
      ...updates
    };
    return this.savePreferences(updated, userId);
  }

  addTimezone(city, userId = null) {
    const current = this.getPreferences(userId);
    const selectedTimezones = [...current.selectedTimezones];
    
    if (!selectedTimezones.find(tz => tz.name === city.name)) {
      selectedTimezones.push(city);
    }

    return this.updatePreferences({ selectedTimezones }, userId);
  }

  removeTimezone(cityName, userId = null) {
    const current = this.getPreferences(userId);
    const selectedTimezones = current.selectedTimezones.filter(
      tz => tz.name !== cityName
    );

    return this.updatePreferences({ selectedTimezones }, userId);
  }

  setHomeTimezone(city, userId = null) {
    return this.updatePreferences({ homeTimezone: city }, userId);
  }

  setTimeFormat(format, userId = null) {
    return this.updatePreferences({ timeFormat: format }, userId);
  }

  setLanguage(language, userId = null) {
    return this.updatePreferences({ language }, userId);
  }

  setDarkMode(enabled, userId = null) {
    return this.updatePreferences({ darkMode: enabled }, userId);
  }

  // Removes only this app's preference keys — never the whole origin.
  clearPreferences(userId = null) {
    try {
      if (userId) {
        this.removeLegacyEncryptedData(userId);
      }
      localStorage.removeItem(this.storageKeyFor(userId));
    } catch (error) {
      console.error('Error clearing preferences:', error);
    }
  }

  migrateAnonymousToUser(userId) {
    try {
      const anonymousPrefs = this.getPreferences();
      
      if (anonymousPrefs.selectedTimezones.length > 0 || 
          anonymousPrefs.homeTimezone || 
          anonymousPrefs.language !== 'en') {
        
        this.savePreferences(anonymousPrefs, userId);
        this.clearPreferences();
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error migrating preferences:', error);
      return false;
    }
  }

  exportPreferences(userId = null) {
    const preferences = this.getPreferences(userId);
    return {
      ...preferences,
      exportedAt: new Date().toISOString()
    };
  }

  importPreferences(data, userId = null) {
    try {
      const validatedData = {
        selectedTimezones: Array.isArray(data.selectedTimezones) ? data.selectedTimezones : [],
        timeFormat: ['12h', '24h'].includes(data.timeFormat) ? data.timeFormat : '12h',
        language: typeof data.language === 'string' ? data.language : 'en',
        homeTimezone: data.homeTimezone || null,
        darkMode: Boolean(data.darkMode),
        cityPresets: Array.isArray(data.cityPresets) ? data.cityPresets : []
      };

      return this.savePreferences(validatedData, userId);
    } catch (error) {
      console.error('Error importing preferences:', error);
      throw error;
    }
  }

  // City Preset Methods (now using database-backed timezone groups)
  async getPresets(userId = null) {
    try {
      return await timezoneGroupsService.getAllGroups();
    } catch (error) {
      console.error('Error getting presets:', error);
      return [];
    }
  }

  async addPreset(name, cities, userId = null) {
    try {
      await timezoneGroupsService.createGroup(name, cities);
      // Update preferences to trigger UI refresh
      return this.getPreferences(userId);
    } catch (error) {
      console.error('Error adding preset:', error);
      throw error;
    }
  }

  async updatePreset(presetId, updates, userId = null) {
    try {
      await timezoneGroupsService.updateGroup(presetId, updates);
      // Update preferences to trigger UI refresh
      return this.getPreferences(userId);
    } catch (error) {
      console.error('Error updating preset:', error);
      throw error;
    }
  }

  async deletePreset(presetId, userId = null) {
    try {
      await timezoneGroupsService.deleteGroup(presetId);
      // Update preferences to trigger UI refresh
      return this.getPreferences(userId);
    } catch (error) {
      console.error('Error deleting preset:', error);
      throw error;
    }
  }

  async loadPreset(presetId, userId = null) {
    try {
      const preset = await timezoneGroupsService.loadGroup(presetId);
      
      // Load the cities from the preset and set as active group
      return this.updatePreferences({ 
        selectedTimezones: preset.cities,
        activeGroupId: presetId
      }, userId);
    } catch (error) {
      console.error('Error loading preset:', error);
      throw error;
    }
  }

  async setActiveGroup(groupId, userId = null) {
    try {
      await timezoneGroupsService.setActiveGroup(groupId);
      return this.updatePreferences({ activeGroupId: groupId }, userId);
    } catch (error) {
      console.error('Error setting active group:', error);
      throw error;
    }
  }

  async getActiveGroup(userId = null) {
    try {
      const activeGroup = await timezoneGroupsService.getActiveGroup();
      return activeGroup ? activeGroup.id : null;
    } catch (error) {
      console.error('Error getting active group:', error);
      return null;
    }
  }

  async hasChangesFromActiveGroup(currentTimezones, userId = null) {
    try {
      const activeGroup = await timezoneGroupsService.getActiveGroup();
      return timezoneGroupsService.hasChangesFromActiveGroup(currentTimezones, activeGroup);
    } catch (error) {
      console.error('Error checking changes from active group:', error);
      return false;
    }
  }

  async updateActiveGroupWithCurrentTimezones(currentTimezones, userId = null) {
    try {
      const activeGroup = await timezoneGroupsService.getActiveGroup();
      await timezoneGroupsService.updateActiveGroupWithCurrentTimezones(currentTimezones, activeGroup);
      // Update preferences to trigger UI refresh
      return this.getPreferences(userId);
    } catch (error) {
      console.error('Error updating active group with current timezones:', error);
      throw error;
    }
  }

  reorderTimezones(newOrder, userId = null) {
    return this.updatePreferences({ 
      selectedTimezones: newOrder 
    }, userId);
  }
}

export const userPreferencesService = new UserPreferencesService();
export default userPreferencesService;